import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PriceInput from "../components/PriceInput";
import { Badge } from "../components/ui/badge";
import {
  Key,
  Users,
  Crown,
  Copy,
  Trash2,
  RefreshCw,
  Plus,
  Search,
  Check,
  X,
  DollarSign,
  Shield,
  Gift,
  Sparkles,
  UserCheck,
  Mail,
  Phone,
  Store,
  Calendar,
  CreditCard,
  Banknote,
  ChevronDown,
  ChevronUp,
  Download,
  Filter,
  Clock,
} from "lucide-react";
import toast from "react-hot-toast";

const API = `${window.location.origin}/api`;

export default function LicenseAdminPage() {
  const [activeTab, setActiveTab] = useState("users"); // users, codes, stats
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Generate codes form
  const [generateForm, setGenerateForm] = useState({
    license_type: "lifetime",
    payment_type: "cash",
    quantity: 1,
    notes: "",
  });
  
  // Activate license form - السعر الموحد 690,000 د.ع
  const [activateForm, setActivateForm] = useState({
    user_email: "",
    license_type: "lifetime",
    payment_type: "cash",
    paid_amount: 690000,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      const [usersRes, codesRes, statsRes] = await Promise.all([
        axios.get(`${API}/license/admin/all-users`, { headers }),
        axios.get(`${API}/license/admin/codes`, { headers }),
        axios.get(`${API}/license/admin/stats`, { headers }),
      ]);
      
      setUsers(usersRes.data.users || []);
      setCodes(codesRes.data.codes || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("حدث خطأ في جلب البيانات");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const generateCodes = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/license/admin/generate-codes`,
        generateForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`تم إنشاء ${generateForm.quantity} كود تفعيل`);
      setShowGenerateModal(false);
      setGenerateForm({ license_type: "lifetime", payment_type: "cash", quantity: 1, notes: "" });
      fetchData();
      
      // نسخ الأكواد للحافظة
      if (response.data.codes?.length > 0) {
        navigator.clipboard.writeText(response.data.codes.join("\n"));
        toast.success("تم نسخ الأكواد للحافظة");
      }
    } catch (error) {
      toast.error("حدث خطأ في إنشاء الأكواد");
    }
  };

  const activateLicense = async () => {
    try {
      const token = localStorage.getItem("token");
      
      // تفعيل الترخيص
      await axios.post(
        `${API}/license/admin/activate-user`,
        activateForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("تم تفعيل الترخيص بنجاح");
      setShowActivateModal(false);
      setActivateForm({ user_email: "", license_type: "lifetime", payment_type: "cash", paid_amount: 690000 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "حدث خطأ");
    }
  };

  const deleteCode = async (code) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا الكود؟")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/license/admin/codes/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("تم حذف الكود");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "حدث خطأ");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("تم نسخ الكود");
  };

  const getLicenseBadge = (type) => {
    switch (type) {
      case "professional":
      case "lifetime":
      case "full":
        return <Badge className="bg-emerald-500 text-white">دائم</Badge>;
      case "regular":
        return <Badge className="bg-blue-500 text-white">مفعّل</Badge>;
      default:
        return <Badge className="bg-slate-500 text-white">تجريبي</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-emerald-500 text-white">فعال</Badge>;
      case "partial":
        return <Badge className="bg-amber-500 text-white">جزئي</Badge>;
      default:
        return <Badge className="bg-slate-500 text-white">غير مفعل</Badge>;
    }
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.store_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredCodes = codes.filter(c =>
    c.code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="license-admin-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
            <Crown className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">إدارة التراخيص</h1>
            <p className="text-slate-400 text-sm">تفعيل التراخيص وإنشاء أكواد التفعيل</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={fetchData}
            disabled={loading}
            className="bg-slate-800 hover:bg-slate-700"
          >
            <RefreshCw className={`w-4 h-4 me-2 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
          <Button
            onClick={() => setShowGenerateModal(true)}
            className="bg-gradient-to-r from-amber-500 to-orange-500"
          >
            <Plus className="w-4 h-4 me-2" />
            إنشاء أكواد
          </Button>
        </div>
      </div>

      {/* إحصائيات سريعة */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-slate-500/10 to-slate-600/10 border-slate-500/30">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 text-slate-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{stats.licenses?.total || 0}</p>
              <p className="text-xs text-slate-400">إجمالي المستخدمين</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border-emerald-500/30">
            <CardContent className="p-4 text-center">
              <Crown className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-emerald-400">{(stats.licenses?.regular || 0) + (stats.licenses?.professional || 0)}</p>
              <p className="text-xs text-slate-400">مفعّل</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border-blue-500/30">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-400">{stats.licenses?.trial || 0}</p>
              <p className="text-xs text-slate-400">فترة تجريبية</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border-emerald-500/30">
            <CardContent className="p-4 text-center">
              <DollarSign className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-emerald-400">{((stats.licenses?.regular || 0) + (stats.licenses?.professional || 0)) * 690000} د.ع</p>
              <p className="text-xs text-slate-400">الإيرادات المتوقعة</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        <Button
          variant={activeTab === "users" ? "default" : "ghost"}
          onClick={() => setActiveTab("users")}
          className={activeTab === "users" ? "bg-emerald-500" : "text-slate-400"}
        >
          <Users className="w-4 h-4 me-2" />
          المستخدمين ({users.length})
        </Button>
        <Button
          variant={activeTab === "codes" ? "default" : "ghost"}
          onClick={() => setActiveTab("codes")}
          className={activeTab === "codes" ? "bg-emerald-500" : "text-slate-400"}
        >
          <Key className="w-4 h-4 me-2" />
          أكواد التفعيل ({codes.length})
        </Button>
      </div>

      {/* بحث */}
      <div className="relative">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder={activeTab === "users" ? "بحث عن مستخدم..." : "بحث عن كود..."}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="bg-slate-800/50 border-slate-700 text-white pr-10"
        />
      </div>

      {/* المحتوى */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-400">جاري التحميل...</p>
        </div>
      ) : activeTab === "users" ? (
        /* جدول المستخدمين */
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="text-right p-4 text-slate-400 font-medium">المستخدم</th>
                    <th className="text-right p-4 text-slate-400 font-medium">المتجر</th>
                    <th className="text-right p-4 text-slate-400 font-medium">الترخيص</th>
                    <th className="text-right p-4 text-slate-400 font-medium">الحالة</th>
                    <th className="text-right p-4 text-slate-400 font-medium">المدفوع</th>
                    <th className="text-right p-4 text-slate-400 font-medium">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => (
                    <tr key={user.id || index} className="border-t border-slate-700 hover:bg-slate-800/50">
                      <td className="p-4">
                        <div>
                          <p className="text-white font-medium">{user.name || "بدون اسم"}</p>
                          <p className="text-slate-400 text-sm">{user.email}</p>
                        </div>
                      </td>
                      <td className="p-4 text-slate-300">{user.store_name || "-"}</td>
                      <td className="p-4">{getLicenseBadge(user.license?.type)}</td>
                      <td className="p-4">{getStatusBadge(user.license?.status)}</td>
                      <td className="p-4">
                        <span className="text-emerald-400 font-medium">
                          ${user.license?.paid_amount || 0}
                        </span>
                        <span className="text-slate-500 text-sm">
                          {" "}/ ${user.license?.total_amount || 0}
                        </span>
                      </td>
                      <td className="p-4">
                        <Button
                          size="sm"
                          onClick={() => {
                            setActivateForm({ ...activateForm, user_email: user.email });
                            setSelectedUser(user);
                            setShowActivateModal(true);
                          }}
                          className="bg-emerald-500 hover:bg-emerald-600"
                        >
                          <UserCheck className="w-4 h-4 me-1" />
                          تفعيل
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* جدول الأكواد */
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="text-right p-4 text-slate-400 font-medium">الكود</th>
                    <th className="text-right p-4 text-slate-400 font-medium">النوع</th>
                    <th className="text-right p-4 text-slate-400 font-medium">الدفع</th>
                    <th className="text-right p-4 text-slate-400 font-medium">الحالة</th>
                    <th className="text-right p-4 text-slate-400 font-medium">التاريخ</th>
                    <th className="text-right p-4 text-slate-400 font-medium">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCodes.map((code, index) => (
                    <tr key={code.code || index} className="border-t border-slate-700 hover:bg-slate-800/50">
                      <td className="p-4">
                        <code className="bg-slate-900 px-3 py-1 rounded text-emerald-400 font-mono">
                          {code.code}
                        </code>
                      </td>
                      <td className="p-4">{getLicenseBadge(code.license_type)}</td>
                      <td className="p-4">
                        <Badge className={code.payment_type === "cash" ? "bg-emerald-500" : "bg-blue-500"}>
                          {code.payment_type === "cash" ? "نقدي" : "أقساط"}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {code.status === "used" ? (
                          <Badge className="bg-slate-500">مستخدم</Badge>
                        ) : (
                          <Badge className="bg-emerald-500">متاح</Badge>
                        )}
                      </td>
                      <td className="p-4 text-slate-400 text-sm">
                        {new Date(code.created_at).toLocaleDateString('ar-IQ')}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyCode(code.code)}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          {code.status === "unused" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteCode(code.code)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal إنشاء أكواد */}
      {showGenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <Card className="w-full max-w-md bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-500" />
                إنشاء أكواد تفعيل
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mb-4">
                <p className="text-emerald-400 text-sm text-center">
                  💡 السعر الموحد: <strong>690,000 دينار عراقي</strong> - ترخيص دائم
                </p>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">نوع الترخيص</label>
                <select
                  value={generateForm.license_type}
                  onChange={(e) => setGenerateForm({...generateForm, license_type: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                >
                  <option value="lifetime">ترخيص دائم (690,000 د.ع)</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">طريقة الدفع</label>
                <select
                  value={generateForm.payment_type}
                  onChange={(e) => setGenerateForm({...generateForm, payment_type: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                >
                  <option value="cash">نقدي (دفعة واحدة - 690,000 د.ع)</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">عدد الأكواد</label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={generateForm.quantity}
                  onChange={(e) => setGenerateForm({...generateForm, quantity: parseInt(e.target.value) || 1})}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">ملاحظات (اختياري)</label>
                <Input
                  value={generateForm.notes}
                  onChange={(e) => setGenerateForm({...generateForm, notes: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="مثال: للعميل أحمد..."
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={() => setShowGenerateModal(false)}
                  variant="outline"
                  className="flex-1 border-slate-600"
                >
                  إلغاء
                </Button>
                <Button
                  onClick={generateCodes}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500"
                >
                  <Gift className="w-4 h-4 me-2" />
                  إنشاء
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal تفعيل ترخيص */}
      {showActivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <Card className="w-full max-w-md bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-emerald-500" />
                تفعيل ترخيص دائم
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mb-4">
                <p className="text-emerald-400 text-sm text-center">
                  💡 السعر الموحد: <strong>690,000 دينار عراقي</strong> - ترخيص دائم
                </p>
              </div>
              {selectedUser && (
                <div className="bg-slate-900/50 rounded-lg p-3 mb-4">
                  <p className="text-white font-medium">{selectedUser.name}</p>
                  <p className="text-slate-400 text-sm">{selectedUser.email}</p>
                  <p className="text-slate-500 text-xs mt-1">{selectedUser.store_name || "بدون متجر"}</p>
                </div>
              )}
              <div>
                <label className="text-sm text-slate-400 mb-1 block">البريد الإلكتروني</label>
                <Input
                  value={activateForm.user_email}
                  onChange={(e) => setActivateForm({...activateForm, user_email: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="example@email.com"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">نوع الترخيص</label>
                <select
                  value={activateForm.license_type}
                  onChange={(e) => setActivateForm({...activateForm, license_type: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                >
                  <option value="lifetime">ترخيص دائم (690,000 د.ع)</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">طريقة الدفع</label>
                <select
                  value={activateForm.payment_type}
                  onChange={(e) => setActivateForm({...activateForm, payment_type: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-4 py-2"
                >
                  <option value="cash">نقدي (دفعة واحدة)</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-400 mb-1 block">المبلغ المدفوع</label>
                <PriceInput
                  value={parseFloat(activateForm.paid_amount) || 0}
                  onChange={(v) => setActivateForm({...activateForm, paid_amount: v})}
                  placeholder="690000"
                  testId="license-paid-amount-input"
                  className="bg-slate-700 border-slate-600 text-white"
                />
                <p className="text-slate-500 text-xs mt-1">الافتراضي: 690,000 د.ع (السعر الكامل للترخيص)</p>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={() => {
                    setShowActivateModal(false);
                    setSelectedUser(null);
                  }}
                  variant="outline"
                  className="flex-1 border-slate-600"
                >
                  إلغاء
                </Button>
                <Button
                  onClick={activateLicense}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600"
                >
                  <Check className="w-4 h-4 me-2" />
                  تفعيل الترخيص
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
